import { dtpPath } from '../../lib/clinicSite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, Check, ArrowRight, AlertCircle, Truck, Package, Calendar, Shield } from 'lucide-react';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import type { DtpProductResponse, DtpCategoryContentResponse } from '@bloom/shared';
import { DtpLayout } from '../../components/dtp/DtpLayout';
import { useEducationalCatalog, getFamilyDisplayName } from '../../components/dtp/educational/useEducationalCatalog';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { dobInputBounds } from '../../utils/dateUtils';
import { dtpApi } from '../../lib/dtpApi';
import { getCurrentTenantConfig } from '../../config/tenant';
import { PatientProtocolBuilder, type PatientProtocolPick } from '../../components/dtp/PatientProtocolBuilder';
import { MembershipPortal } from '../../components/dtp/MembershipPortal';
import { presetKeyForFamilySlug } from '../../data/peptidePresetSlugMap';
import {
  wouldExceedWeightLossCap,
  WEIGHT_LOSS_CAP_MESSAGE
} from '../../utils/membershipConstraints';
import { parseRedirectError, formatErrorForDisplay } from '../../lib/hyperswitchErrors';

type Tier = 1 | 2 | 3;
type ProfileGateField = 'email' | 'firstName' | 'lastName' | 'dateOfBirth' | 'address';

const TIERS: Array<{ tier: Tier; productCount: 1 | 2 | 4; pricePerCycleCents: number; tagline: string }> = [
  { tier: 1, productCount: 1, pricePerCycleCents: 20000, tagline: 'Start with one peptide.' },
  { tier: 2, productCount: 2, pricePerCycleCents: 30000, tagline: 'Stack two for layered effect.' },
  { tier: 3, productCount: 4, pricePerCycleCents: 40000, tagline: 'Full protocol — four peptides.' }
];

type Step = 'choose' | 'card' | 'submitted';

// sessionStorage key for the in-progress signup draft. Scoped per physician
// so switching clinics doesn't restore a stale draft for the wrong store.
function draftStorageKey(physicianId: string | null | undefined): string {
  return `dtp:membership:draft:${physicianId || 'unknown'}`;
}

interface MembershipDraft {
  tier: Tier | null;
  picked: string[];
  pickMode: 'suggest' | 'self';
  selectedGoals: string[];
}

function loadDraft(physicianId: string | null | undefined): MembershipDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(draftStorageKey(physicianId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MembershipDraft;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveDraft(physicianId: string | null | undefined, draft: MembershipDraft): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(draftStorageKey(physicianId), JSON.stringify(draft));
  } catch {
    /* sessionStorage full / disabled — ignore */
  }
}

function clearDraft(physicianId: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(draftStorageKey(physicianId));
  } catch {
    /* ignore */
  }
}

export default function DtpMembership() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, error, clinic, products, categoryContent, physicianId } = useEducationalCatalog();
  const { isAuthenticated, patient } = useDtpAuthStore();

  // Restore any draft saved before login/redirect. We hydrate synchronously on
  // first render so the picker shows the patient's prior selections without a
  // flash of empty state.
  const initialDraft = useMemo(() => loadDraft(physicianId), [physicianId]);

  const [tier, setTier] = useState<Tier | null>(initialDraft?.tier ?? null);
  const [picked, setPicked] = useState<string[]>(initialDraft?.picked ?? []);
  const [pickMode, setPickMode] = useState<'suggest' | 'self'>(initialDraft?.pickMode ?? 'suggest');
  const [selectedGoals, setSelectedGoals] = useState<string[]>(initialDraft?.selectedGoals ?? []);
  const [step, setStep] = useState<Step>('choose');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Informational (not an error): shown when a pick is blocked by the one
  // weight-loss-peptide-per-membership rule. Kept separate from submitError so
  // it renders in a neutral style instead of a red failure banner.
  const [capNotice, setCapNotice] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [hyperPromise, setHyperPromise] = useState<any>(null);
  const [membershipId, setMembershipId] = useState<string | null>(null);
  // Card decline copy surfaced from the Hyperswitch return_url (?redirect_status=failed).
  const [declineMessage, setDeclineMessage] = useState<string | null>(null);
  // Which profile fields the backend told us are missing — when populated we
  // render an inline ProfileCompletionGate instead of bouncing the user with
  // a generic "Could not start card setup" banner.
  const [missingProfileFields, setMissingProfileFields] = useState<ProfileGateField[] | null>(null);
  // SDK load fallback — flip true when loadHyper hasn't resolved within the
  // timeout so the card step can surface an actionable error instead of a
  // spinner-of-doom.
  const [sdkLoadFailed, setSdkLoadFailed] = useState(false);
  // The patient's current membership (if any). When this is populated we show a
  // status panel instead of the tier picker — otherwise hitting "Continue to
  // card" returns "Patient already has an active or pending membership" with
  // no UI to act on it.
  const [existingMembership, setExistingMembership] = useState<any | null>(null);
  const [existingLoading, setExistingLoading] = useState(false);
  const [existingError, setExistingError] = useState('');
  // Flips true once the existing-membership lookup has resolved at least once,
  // so the post-OTP auto-resume can wait for it instead of racing the fetch.
  const [existingChecked, setExistingChecked] = useState(false);
  // Guards the one-shot auto-resume so it fires at most once per mount.
  const resumedRef = useRef(false);

  const eligible = useMemo(() => products.filter(p => p.membershipEligible), [products]);
  const tierConfig = tier ? TIERS.find(t => t.tier === tier)! : null;
  const requiredCount = tierConfig?.productCount ?? 0;

  // Group eligible peptides by category for the picker. Falls back to a
  // humanized slug if no DtpCategoryContent was authored for that slug.
  const eligibleByCategory = useMemo(() => {
    const catBySlug = new Map<string, DtpCategoryContentResponse>(
      categoryContent.map(c => [c.slug, c])
    );
    const groups = new Map<string, { displayName: string; order: number; items: DtpProductResponse[] }>();
    for (const p of eligible) {
      const slug = p.primaryCategorySlug || 'peptides';
      if (!groups.has(slug)) {
        const cat = catBySlug.get(slug);
        groups.set(slug, {
          displayName: cat?.displayName || humanizeSlug(slug),
          order: cat?.order ?? 999,
          items: []
        });
      }
      groups.get(slug)!.items.push(p);
    }
    return Array.from(groups.entries())
      .map(([slug, g]) => ({ slug, ...g }))
      .sort((a, b) => a.order - b.order || a.displayName.localeCompare(b.displayName));
  }, [eligible, categoryContent]);

  // Persist signup draft across navigation (e.g. patient picks tier+peptides
  // while logged out, gets routed through OTP/signup, lands back here). We
  // write on every relevant state change; the corresponding read happens once
  // on mount via the initialDraft seed above.
  useEffect(() => {
    // Only persist while the patient is still on the picker — once they've
    // submitted (step==='card') the draft is no longer the source of truth
    // and we want a clean slate on a future re-entry.
    if (step !== 'choose') return;
    saveDraft(physicianId, { tier, picked, pickMode, selectedGoals });
  }, [physicianId, step, tier, picked, pickMode, selectedGoals]);

  // Hyperswitch appends ?redirect_status=succeeded|failed after the card-save
  // flow returns to us. On success we send the patient to the portal; on
  // failure we surface the decline and re-arm the card form with a fresh
  // setup intent so they can retry without losing context.
  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    const returnedMembershipId = searchParams.get('id');
    if (!redirectStatus) return;

    // Strip the params off the URL so a refresh doesn't replay this branch.
    const cleanParams = new URLSearchParams(searchParams);
    cleanParams.delete('redirect_status');
    cleanParams.delete('error_code');
    cleanParams.delete('error_message');
    cleanParams.delete('payment_id');
    cleanParams.delete('payment_intent_client_secret');
    cleanParams.delete('id');

    if (redirectStatus === 'succeeded') {
      // Card vaulted — membership is being activated server-side. Sweep the
      // draft and bounce to the portal which surfaces the new active state.
      clearDraft(physicianId);
      setSearchParams(cleanParams, { replace: true });
      navigate(dtpPath(physicianId, `/portal?just_signed_up=1&id=${returnedMembershipId || ''}`), { replace: true });
      return;
    }

    // Failed (declined card, 3DS rejection, etc.). Pull a user-friendly
    // message off the URL and start a fresh card-setup so the form is ready
    // to retry.
    const parsed = parseRedirectError(searchParams);
    const display = parsed ? formatErrorForDisplay(parsed) : null;
    setDeclineMessage(display?.description || 'Your card was declined. Please try a different card.');
    setSearchParams(cleanParams, { replace: true });

    if (returnedMembershipId) {
      setMembershipId(returnedMembershipId);
      // Step into the card form immediately so the MembershipPortal panel
      // doesn't flash before the fresh setup intent resolves.
      setStep('card');
      // Pull a fresh setup intent — the previous one is consumed.
      dtpApi.startReplacePaymentMethod()
        .then((res: any) => {
          if (res?.success && res.clientSecret) {
            setClientSecret(res.clientSecret);
          } else {
            setSubmitError('Could not restart card setup. Please refresh and try again.');
          }
        })
        .catch(() => {
          setSubmitError('Could not restart card setup. Please refresh and try again.');
        });
    }
  }, [searchParams, setSearchParams, navigate, physicianId]);

  // Fetch the patient's existing membership so we can render a status panel
  // (and skip the picker) for anyone with an active or pending one. Exposed as
  // a callback so the card step's "Back" button can refresh it — once a
  // pending membership exists, stepping back should surface the portal (where
  // tier/selection can be changed) rather than the bare picker.
  const refreshExistingMembership = useCallback(async () => {
    if (!isAuthenticated || !patient) {
      setExistingMembership(null);
      setExistingError('');
      return;
    }
    setExistingLoading(true);
    setExistingError('');
    try {
      const res = await dtpApi.getMyMembership();
      if (!res.success) throw new Error('Membership unavailable');
      setExistingMembership(res.membership);
    } catch {
      setExistingError('We couldn’t load your membership. Please try again.');
    } finally {
      setExistingLoading(false);
      setExistingChecked(true);
    }
  }, [isAuthenticated, patient]);

  useEffect(() => { void refreshExistingMembership(); }, [refreshExistingMembership]);

  // Post-OTP resume: the patient picked a tier + peptides while logged out, got
  // routed through the checkout OTP flow, and landed back here with ?resume=card.
  // Their picks are already restored from the sessionStorage draft, so rather
  // than dropping them on the picker to click "Continue to card" a second time,
  // advance straight into card setup. We wait for the membership lookup to
  // resolve (existingChecked) so we don't auto-advance someone who already has
  // one, and fall back to the picker if the draft didn't survive the round-trip.
  useEffect(() => {
    if (resumedRef.current || loading || clinic?.membershipEnabled !== true) return;
    if (searchParams.get('resume') !== 'card') return;
    if (!isAuthenticated || !patient || !existingChecked || existingError) return;
    // Consume the signal regardless of outcome so a refresh can't replay it.
    resumedRef.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('resume');
    setSearchParams(next, { replace: true });
    if (existingMembership) return;           // already a member → portal shows
    if (step !== 'choose') return;
    if (!tier || picked.length !== requiredCount) return; // draft lost → leave on picker
    void startSignup();
  }, [searchParams, isAuthenticated, patient, existingChecked, existingError, existingMembership, step, tier, picked, requiredCount, setSearchParams, loading, clinic?.membershipEnabled]);

  // Load hyperswitch SDK once user enters card step
  useEffect(() => {
    if (step !== 'card' || hyperPromise) return;
    try {
      const cfg = getCurrentTenantConfig();
      const env = cfg.payments.hyperswitch.environment;
      const customBackendUrl = env === 'sandbox' ? 'https://sandbox.hyperswitch.io' : 'https://api.hyperswitch.io';
      setHyperPromise(loadHyper(cfg.payments.hyperswitch.publishableKey, { customBackendUrl }));
    } catch (e) {
      setSubmitError('Payment system unavailable.');
    }
  }, [step, hyperPromise]);

  // SDK load watchdog: if the card step is still waiting on the SDK or the
  // setup client_secret after 12s, flip into an actionable error state instead
  // of leaving the user with an indefinite spinner. The retry button below
  // clears the flag and re-runs the load via loadHyper + startReplacePaymentMethod.
  useEffect(() => {
    if (step !== 'card') {
      setSdkLoadFailed(false);
      return;
    }
    if (hyperPromise && clientSecret) return;
    const timer = setTimeout(() => {
      if (!hyperPromise || !clientSecret) setSdkLoadFailed(true);
    }, 12000);
    return () => clearTimeout(timer);
  }, [step, hyperPromise, clientSecret]);

  async function retryCardLoad() {
    setSdkLoadFailed(false);
    setSubmitError(null);
    try {
      const cfg = getCurrentTenantConfig();
      const env = cfg.payments.hyperswitch.environment;
      const customBackendUrl = env === 'sandbox' ? 'https://sandbox.hyperswitch.io' : 'https://api.hyperswitch.io';
      setHyperPromise(loadHyper(cfg.payments.hyperswitch.publishableKey, { customBackendUrl }));
      // Re-fetch a fresh setup intent — the previous one may have expired or
      // the original signup call may have failed before returning one.
      const res: any = await dtpApi.startReplacePaymentMethod();
      if (res?.success && res.clientSecret) {
        setClientSecret(res.clientSecret);
      } else {
        setSubmitError('Could not restart card setup. Please refresh and try again.');
        setSdkLoadFailed(true);
      }
    } catch {
      setSubmitError('Could not reach the payment provider. Check your connection and retry.');
      setSdkLoadFailed(true);
    }
  }

  function togglePick(productId: string) {
    if (!tierConfig) return;
    setPicked(prev => {
      if (prev.includes(productId)) return prev.filter(id => id !== productId);
      if (prev.length >= tierConfig.productCount) return prev;
      if (wouldExceedWeightLossCap(productId, prev, products)) {
        setCapNotice(WEIGHT_LOSS_CAP_MESSAGE);
        return prev;
      }
      // Clear the cap notice once a valid pick lands.
      setCapNotice(null);
      return [...prev, productId];
    });
  }

  async function startSignup() {
    if (!tier || picked.length !== requiredCount) return;
    if (!isAuthenticated || !patient) {
      // Send patient through OTP flow; existing checkout handles signup completion
      // and screening. After login they can return here. Pass the intent in the URL
      // so the post-login redirect can land back on this page.
      navigate(dtpPath(physicianId, `/checkout?intent=membership`));
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setMissingProfileFields(null);
    try {
      const res = await dtpApi.createMembership({
        tier,
        selectedProductIds: picked,
        ...(selectedGoals.length > 0 && { selectedGoals })
      });
      // Profile gate: backend says we need more fields before we can vault a
      // card. Render the inline gate; don't surface a generic error.
      if (!res.success && res.code === 'profile_incomplete' && res.missingFields) {
        setMissingProfileFields(res.missingFields);
        return;
      }
      if (!res.success || !res.membership) throw new Error(res.error || 'Signup failed');
      setMembershipId(res.membership._id);
      if (res.setupClientSecret) {
        // Successfully created the membership — burn the sessionStorage draft
        // so a back-and-refresh doesn't re-seed picks the patient has already
        // committed to.
        clearDraft(physicianId);
        setClientSecret(res.setupClientSecret);
        setStep('card');
      } else {
        setSubmitError('Could not start card setup. Please try again.');
      }
    } catch (e: any) {
      setSubmitError(e?.response?.data?.error || e?.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <DtpLayout><div className="min-h-[240px] flex items-center justify-center" role="status" aria-label="Loading membership">
      <Loader2 className="w-8 h-8 animate-spin text-[var(--store-accent,#2563eb)]" />
    </div></DtpLayout>
  );

  if (error || !clinic) return (
    <DtpLayout><div className="min-h-[240px] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <h1 className="text-2xl text-[var(--store-text,#111827)]">{error || 'Store unavailable'}</h1>
      </div>
    </div></DtpLayout>
  );

  if (existingError) return (
    <DtpLayout membershipEnabled={clinic.membershipEnabled === true}>
      <section className="space-y-4">
        <h1 className="text-3xl font-semibold">Membership</h1>
        <p role="alert" className="mt-4 text-red-700">{existingError}</p>
        <button onClick={refreshExistingMembership} disabled={existingLoading} className="min-h-[44px] underline">{existingLoading ? 'Loading…' : 'Try again'}</button>
      </section>
    </DtpLayout>
  );

  if (clinic.membershipEnabled !== true && !existingMembership && !existingLoading && (!isAuthenticated || existingChecked)) return (
    <DtpLayout membershipEnabled={false}>
      <section className="space-y-4">
        <h1 className="text-3xl font-semibold">Membership</h1>
        <p className="mt-4 text-gray-600">This clinic isn’t offering new memberships. Your orders and results are still available in your patient portal.</p>
      </section>
    </DtpLayout>
  );

  return (
    <DtpLayout membershipEnabled={clinic.membershipEnabled === true}>
      {/* Managing a membership reads like the other portal pages; choosing one needs the width for the tier grid. */}
      <section className={existingMembership ? 'mx-auto max-w-3xl pb-8' : 'pb-8'}>
        <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">
          {existingMembership ? 'Your membership' : 'Membership'}
        </h1>
        <p className="mt-3 max-w-2xl text-[17px] text-[var(--portal-muted)]">
          {existingMembership ? 'Manage your next cycle, payment method, and preferences.' : `Pick a tier, pick your peptides, ship every 45 days. Approved by ${clinic.name}.`}
        </p>

        {step === 'choose' && existingMembership && (
          <MembershipPortal
            membership={existingMembership}
            eligible={eligible}
            allProducts={products}
            onChange={(m) => setExistingMembership(m)}
            onCancelled={() => setExistingMembership(null)}
          />
        )}

        {step === 'choose' && (existingLoading || (isAuthenticated && !existingChecked)) && !existingMembership && (
          <div className="mt-12 flex items-center gap-2 text-[var(--store-muted,#6b7280)]">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your membership status…
          </div>
        )}

        {step === 'choose' && !existingMembership && !existingLoading && (!isAuthenticated || existingChecked) && missingProfileFields && (
          <ProfileCompletionGate
            missingFields={missingProfileFields}
            patient={patient}
            onCompleted={() => {
              setMissingProfileFields(null);
              // Re-attempt signup with the same picks now that the profile is filled.
              startSignup();
            }}
            onCancel={() => setMissingProfileFields(null)}
          />
        )}

        {step === 'choose' && !existingMembership && !existingLoading && (!isAuthenticated || existingChecked) && !missingProfileFields && (
          <>
            {/* Tier picker */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
              {TIERS.map(t => {
                const selected = tier === t.tier;
                return (
                  <button
                    key={t.tier}
                    onClick={() => { setTier(t.tier); setPicked([]); }}
                    className={`text-left p-6 rounded-2xl border transition ${
                      selected ? 'border-[var(--store-accent,#2563eb)] bg-white shadow-sm' : 'border-[var(--store-border,#e5e7eb)] hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-baseline justify-between">
                      <h3 className="text-2xl font-medium">Tier {t.tier}</h3>
                      {selected && <Check className="w-5 h-5 text-[var(--store-accent,#2563eb)]" />}
                    </div>
                    <div className="mt-1 text-sm text-[var(--store-muted,#6b7280)]">
                      {t.productCount} peptide{t.productCount > 1 ? 's' : ''} every 45 days
                    </div>
                    <div className="mt-4 text-3xl">${(t.pricePerCycleCents / 100).toFixed(0)}</div>
                    <div className="text-sm text-gray-600 mt-2">{t.tagline}</div>
                  </button>
                );
              })}
            </div>

            {/* Peptide picker — grouped by category */}
            {tier && (
              <div className="mt-12 pb-32 md:pb-0">
                <div className="flex items-baseline justify-between mb-6">
                  <h2 className="text-2xl md:text-3xl font-medium">
                    Choose {requiredCount} peptide{requiredCount > 1 ? 's' : ''}
                  </h2>
                  <div className="text-sm text-[var(--store-muted,#6b7280)]">
                    {picked.length} / {requiredCount}
                  </div>
                </div>

                {/* Suggest vs self-pick toggle */}
                <div className="inline-flex border border-[var(--store-border,#e5e7eb)] rounded-full p-0.5 bg-white mb-6">
                  <button
                    onClick={() => { setPickMode('suggest'); setPicked([]); }}
                    className={`px-4 py-1.5 text-sm rounded-full transition ${
                      pickMode === 'suggest' ? 'bg-[var(--store-accent,#2563eb)] text-white' : 'text-gray-600'
                    }`}
                  >
                    Suggest for me
                  </button>
                  <button
                    onClick={() => { setPickMode('self'); setPicked([]); }}
                    className={`px-4 py-1.5 text-sm rounded-full transition ${
                      pickMode === 'self' ? 'bg-[var(--store-accent,#2563eb)] text-white' : 'text-gray-600'
                    }`}
                  >
                    Pick myself
                  </button>
                </div>

                {/* Suggest-a-protocol path */}
                {pickMode === 'suggest' && eligible.length > 0 && (
                  <SuggestPath
                    eligible={eligible}
                    allProducts={products}
                    requiredCount={requiredCount}
                    selectedGoals={selectedGoals}
                    initialSelectedIds={picked}
                    onGoalsChange={setSelectedGoals}
                    onPicksChange={(picks) => setPicked(picks.map(p => p.productId))}
                  />
                )}

                {/* Self-pick path */}
                {pickMode === 'self' && (eligible.length === 0 ? (
                  <div className="text-center py-16 text-[var(--store-muted,#6b7280)] border border-dashed border-[var(--store-border,#e5e7eb)] rounded-xl">
                    No membership-eligible peptides in this clinic yet.
                  </div>
                ) : (
                  <div className="space-y-10">
                    {eligibleByCategory.map(group => (
                      <div key={group.slug}>
                        <div className="flex items-baseline gap-3 mb-3">
                          <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--store-muted,#6b7280)]">{group.displayName}</div>
                          <div className="h-px flex-1 bg-[var(--store-border,#e5e7eb)]" />
                          <div className="text-xs text-[var(--store-muted,#6b7280)]">{group.items.length}</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {group.items.map(p => {
                            const selected = picked.includes(p._id);
                            const atLimit = !selected && picked.length >= requiredCount;
                            const blockedByWeightLossCap =
                              !selected && wouldExceedWeightLossCap(p._id, picked, products);
                            const disabled = atLimit || blockedByWeightLossCap;
                            const familyName = p.drugFamilySlug
                              ? getFamilyDisplayName(p.drugFamilySlug, products)
                              : p.drugName;
                            return (
                              <button
                                key={p._id}
                                onClick={() => togglePick(p._id)}
                                disabled={disabled}
                                aria-pressed={selected}
                                title={blockedByWeightLossCap ? WEIGHT_LOSS_CAP_MESSAGE : undefined}
                                className={`relative text-left p-4 md:p-5 rounded-xl border transition active:scale-[0.99] ${
                                  selected
                                    ? 'border-[var(--store-accent,#2563eb)] bg-gray-50 shadow-sm'
                                    : 'border-[var(--store-border,#e5e7eb)] bg-white hover:border-gray-400'
                                } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                              >
                                {selected && (
                                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[var(--store-accent,#2563eb)] flex items-center justify-center">
                                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                                  </div>
                                )}
                                <div className="font-medium text-[15px] pr-7">{familyName}</div>
                                <div className="text-xs text-[var(--store-muted,#6b7280)] mt-1">
                                  {p.concentration} · {p.size}
                                </div>
                                {blockedByWeightLossCap && (
                                  <div className="mt-2 text-[11px] text-[var(--store-muted,#6b7280)]">
                                    {WEIGHT_LOSS_CAP_MESSAGE}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                {/* What's included */}
                <div className="mt-12 rounded-2xl border border-[var(--store-border,#e5e7eb)] bg-white p-5 md:p-6">
                  <h3 className="text-lg md:text-xl font-medium mb-4">What's included</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    <Included icon={Truck} title="Free shipping" body="Discreet, to your door." />
                    <Included icon={Package} title="Supplies included" body="Syringes, alcohol pads, sharps container." />
                    <Included icon={Calendar} title="Every 45 days" body="Skip, swap, or pause anytime." />
                    <Included icon={Shield} title="Physician oversight" body={`${clinic.name} reviews each membership.`} />
                  </div>
                </div>

                {/* Desktop CTA row */}
                <div className="hidden md:flex mt-10 items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    ${tierConfig ? (tierConfig.pricePerCycleCents / 100).toFixed(0) : 0} every 45 days. Cancel anytime.
                  </div>
                  <button
                    onClick={startSignup}
                    disabled={picked.length !== requiredCount || submitting}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--store-accent,#2563eb)] text-white text-sm font-medium disabled:opacity-50"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {isAuthenticated ? 'Continue to card' : 'Sign in to continue'}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {capNotice && (
                  <div className="mt-4 flex items-start gap-2 text-sm text-[#7a6a3a] bg-[#fbf6e7] border border-[#ece0bd] rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{capNotice}</span>
                  </div>
                )}

                {submitError && (
                  <div className="mt-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {step === 'card' && (
          <div className="mt-12 max-w-xl">
            <button
              type="button"
              onClick={() => { setStep('choose'); void refreshExistingMembership(); }}
              className="inline-flex items-center gap-1 text-sm text-[var(--store-muted,#6b7280)] hover:text-gray-600 mb-4"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back
            </button>
            <h2 className="text-2xl font-medium mb-1">Save a card</h2>
            <p className="text-sm text-[var(--store-muted,#6b7280)] mb-6">
              We'll verify with a $1 hold (refunded automatically). Your card is charged once {clinic.name} approves.
            </p>
            {declineMessage && (
              <div className="mb-5 flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium">Card declined</div>
                  <div className="mt-0.5">{declineMessage}</div>
                </div>
              </div>
            )}
            {submitError && (
              <div className="mb-5 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{submitError}</span>
              </div>
            )}
            {hyperPromise && clientSecret ? (
              <HyperElements
                hyper={hyperPromise}
                options={{
                  publishableKey: getCurrentTenantConfig().payments.hyperswitch.publishableKey,
                  clientSecret,
                  loader: 'auto'
                }}
              >
                <MembershipCardForm
                  returnUrl={`${window.location.origin}${dtpPath(physicianId, `/membership?id=${membershipId}`)}`}
                />
              </HyperElements>
            ) : sdkLoadFailed ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium">Couldn't reach the payment provider</div>
                    <div className="mt-1 text-red-700">
                      This usually clears up on its own. If a browser extension or your network is blocking
                      <code className="mx-1 px-1 rounded bg-white/60">hyperswitch.io</code>, try a different
                      browser or disable the extension.
                    </div>
                    <button
                      onClick={retryCardLoad}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-red-700 text-white text-sm font-medium hover:bg-red-800"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--store-accent,#2563eb)]" />
              </div>
            )}
          </div>
        )}
      </section>

      {/* Sticky mobile CTA — shows once a tier is picked, only on /choose step */}
      {step === 'choose' && tier && (
        <div className="md:hidden fixed inset-x-0 bottom-0 z-30 bg-white/95 backdrop-blur border-t border-[var(--store-border,#e5e7eb)] px-4 py-3 pb-[max(env(safe-area-inset-bottom),12px)] shadow-[0_-8px_24px_-12px_rgba(26,22,18,0.15)]">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--store-muted,#6b7280)]">
                {picked.length} / {requiredCount} selected
              </div>
              <div className="text-sm font-medium text-[var(--store-text,#111827)]">
                ${tierConfig ? (tierConfig.pricePerCycleCents / 100).toFixed(0) : 0} every 45 days
              </div>
            </div>
            <button
              onClick={startSignup}
              disabled={picked.length !== requiredCount || submitting}
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[var(--store-accent,#2563eb)] text-white text-sm font-medium disabled:opacity-40"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isAuthenticated ? 'Continue' : 'Sign in'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </DtpLayout>
  );
}

function Included({ icon: Icon, title, body }: { icon: React.ComponentType<{ className?: string }>; title: string; body: string }) {
  return (
    <div>
      <div className="w-9 h-9 rounded-full bg-gray-50 text-[var(--store-accent,#2563eb)] flex items-center justify-center mb-2">
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-sm font-medium text-[var(--store-text,#111827)]">{title}</div>
      <div className="text-xs text-gray-600 mt-0.5 leading-relaxed">{body}</div>
    </div>
  );
}

// Goals offered to membership patients. Each maps to one or more
// primaryCategorySlug values used to filter eligible peptides.
const GOAL_OPTIONS: Array<{ key: string; label: string; categorySlugs: string[] }> = [
  { key: 'weight', label: 'Weight management', categorySlugs: ['weight-loss'] },
  { key: 'gh', label: 'Growth hormone', categorySlugs: ['peptides-growth-hormone'] },
  { key: 'healing', label: 'Recovery & healing', categorySlugs: ['peptides-healing'] },
  { key: 'metabolic', label: 'Energy & metabolism', categorySlugs: ['peptides-metabolism'] },
  { key: 'collagen', label: 'Skin & collagen', categorySlugs: ['peptides-collagen'] }
];

function SuggestPath({
  eligible,
  allProducts,
  requiredCount,
  selectedGoals,
  initialSelectedIds,
  onGoalsChange,
  onPicksChange
}: {
  eligible: DtpProductResponse[];
  allProducts: DtpProductResponse[];
  requiredCount: number;
  selectedGoals: string[];
  initialSelectedIds: string[];
  onGoalsChange: (goals: string[]) => void;
  onPicksChange: (picks: PatientProtocolPick[]) => void;
}) {
  const matchingSlugs = useMemo(() => {
    if (selectedGoals.length === 0) return null; // null = "no filter, show all"
    const slugs = new Set<string>();
    for (const g of selectedGoals) {
      const opt = GOAL_OPTIONS.find(o => o.key === g);
      if (opt) opt.categorySlugs.forEach(s => slugs.add(s));
    }
    return slugs;
  }, [selectedGoals]);

  const candidates = useMemo(() => {
    let list = eligible;
    if (matchingSlugs) list = list.filter(p => p.primaryCategorySlug && matchingSlugs.has(p.primaryCategorySlug));
    // Prefer peptides we have presets for so the protocol builder shows real
    // suggestions; un-presetted ones still appear at the bottom for completeness.
    return [
      ...list.filter(p => presetKeyForFamilySlug(p.drugFamilySlug)),
      ...list.filter(p => !presetKeyForFamilySlug(p.drugFamilySlug))
    ];
  }, [eligible, matchingSlugs]);

  function toggleGoal(key: string) {
    onGoalsChange(
      selectedGoals.includes(key)
        ? selectedGoals.filter(g => g !== key)
        : [...selectedGoals, key]
    );
  }

  return (
    <div className="space-y-6">
      {/* Goal chips */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--store-muted,#6b7280)] mb-2">What are you focused on?</div>
        <div className="flex flex-wrap gap-2">
          {GOAL_OPTIONS.map(opt => {
            const active = selectedGoals.includes(opt.key);
            return (
              <button
                key={opt.key}
                onClick={() => toggleGoal(opt.key)}
                className={`px-3 py-1.5 rounded-full text-sm border transition ${
                  active
                    ? 'border-[var(--store-accent,#2563eb)] bg-[var(--store-accent,#2563eb)] text-white'
                    : 'border-[var(--store-border,#e5e7eb)] bg-white text-gray-600 hover:border-gray-400'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div className="text-xs text-[var(--store-muted,#6b7280)] mt-2">
          Pick any combination — or leave all unselected to see everything.
        </div>
      </div>

      {candidates.length === 0 ? (
        <div className="text-center py-12 text-[var(--store-muted,#6b7280)] border border-dashed border-[var(--store-border,#e5e7eb)] rounded-xl">
          No peptides match those goals. Try selecting more goals or "Pick myself".
        </div>
      ) : (
        <PatientProtocolBuilder
          candidates={candidates}
          requiredCount={requiredCount}
          initialSelectedIds={initialSelectedIds}
          allProducts={allProducts}
          onPicksChange={onPicksChange}
        />
      )}
    </div>
  );
}

function humanizeSlug(slug: string): string {
  // 'peptides-growth-hormone' -> 'Growth Hormone'
  return slug.replace(/^peptides-/, '').split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

function MembershipCardForm({ returnUrl }: { returnUrl: string }) {
  return (
    <UnifiedCheckout
      id="dtp-membership-card"
      options={{
        layout: { type: 'tabs' },
        paymentMethodOrder: ['card'],
        sdkHandleConfirmPayment: {
          handleConfirm: true,
          buttonText: 'Save card',
          confirmParams: { return_url: returnUrl }
        }
      } as any}
    />
  );
}

/**
 * Inline form rendered when the backend reports membership signup is blocked
 * on missing profile fields. Renders ONLY the fields the backend asked for so
 * we don't re-prompt for things the patient already filled at OTP signup.
 * On submit, PATCHes /patient/me/profile and calls onCompleted, which re-runs
 * the membership signup.
 */
function ProfileCompletionGate({
  missingFields,
  patient,
  onCompleted,
  onCancel
}: {
  missingFields: ProfileGateField[];
  patient: any;
  onCompleted: () => void;
  onCancel: () => void;
}) {
  const needsEmail = missingFields.includes('email');
  const needsFirstName = missingFields.includes('firstName');
  const needsLastName = missingFields.includes('lastName');
  const needsDob = missingFields.includes('dateOfBirth');
  const needsAddress = missingFields.includes('address');

  const [email, setEmail] = useState<string>(patient?.email || '');
  const [firstName, setFirstName] = useState<string>(patient?.firstName || '');
  const [lastName, setLastName] = useState<string>(patient?.lastName || '');
  const [dateOfBirth, setDateOfBirth] = useState<string>(() => {
    const dob = patient?.dateOfBirth;
    if (!dob) return '';
    const d = new Date(dob);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  });
  const [street, setStreet] = useState<string>(patient?.address?.street || '');
  const [street2, setStreet2] = useState<string>(patient?.address?.street2 || '');
  const [city, setCity] = useState<string>(patient?.address?.city || '');
  const [state, setState] = useState<string>(patient?.address?.state || '');
  const [zipCode, setZipCode] = useState<string>(patient?.address?.zipCode || '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Lightweight client-side validation matched to what the backend will
    // accept — keeps an obvious bad submit from making a round-trip.
    if (needsEmail) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setError('Enter a valid email address.'); return;
      }
    }
    if (needsFirstName && !firstName.trim()) { setError('First name is required.'); return; }
    if (needsLastName && !lastName.trim()) { setError('Last name is required.'); return; }
    if (needsDob && !dateOfBirth) { setError('Date of birth is required.'); return; }
    if (needsAddress) {
      if (!street.trim() || !city.trim() || !state.trim() || !zipCode.trim()) {
        setError('Complete street, city, state, and ZIP.'); return;
      }
      if (state.trim().length !== 2) { setError('Use the 2-letter state code (e.g. CA).'); return; }
    }

    const body: Parameters<typeof dtpApi.updatePatientProfile>[0] = {};
    if (needsEmail) body.email = email.trim();
    if (needsFirstName) body.firstName = firstName.trim();
    if (needsLastName) body.lastName = lastName.trim();
    if (needsDob) body.dateOfBirth = dateOfBirth;
    if (needsAddress) {
      body.address = {
        street: street.trim(),
        ...(street2.trim() ? { street2: street2.trim() } : {}),
        city: city.trim(),
        state: state.trim().toUpperCase(),
        zipCode: zipCode.trim()
      };
    }

    setSaving(true);
    try {
      const res = await dtpApi.updatePatientProfile(body);
      if (!res.success) throw new Error(res.error || 'Could not save profile');
      onCompleted();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save profile');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={save} className="mt-12 max-w-xl space-y-5 rounded-2xl border border-[var(--store-border,#e5e7eb)] bg-white p-5 md:p-6">
      <div>
        <h2 className="text-2xl font-medium">Finish setting up your account</h2>
        <p className="mt-1 text-sm text-[var(--store-muted,#6b7280)]">
          We need a few more details before we can save your card and start your membership.
        </p>
      </div>

      {needsEmail && (
        <div>
          <label className="block text-sm font-medium mb-1">Email *</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
            placeholder="you@example.com"
            autoFocus
          />
          <p className="text-xs text-[var(--store-muted,#6b7280)] mt-1">Used for receipts and shipping updates.</p>
        </div>
      )}

      {(needsFirstName || needsLastName) && (
        <div className="grid grid-cols-2 gap-3">
          {needsFirstName && (
            <div>
              <label className="block text-sm font-medium mb-1">First name *</label>
              <input
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
              />
            </div>
          )}
          {needsLastName && (
            <div>
              <label className="block text-sm font-medium mb-1">Last name *</label>
              <input
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
              />
            </div>
          )}
        </div>
      )}

      {needsDob && (
        <div>
          <label className="block text-sm font-medium mb-1">Date of birth *</label>
          <input
            type="date"
            {...dobInputBounds()}
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
          />
        </div>
      )}

      {needsAddress && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Shipping address *</label>
            <input
              value={street}
              onChange={e => setStreet(e.target.value)}
              placeholder="Street address"
              className="w-full px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
            />
          </div>
          <div>
            <input
              value={street2}
              onChange={e => setStreet2(e.target.value)}
              placeholder="Apt / Suite (optional)"
              className="w-full px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <input
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder="City"
              className="px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
            />
            <input
              value={state}
              onChange={e => setState(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="ST"
              maxLength={2}
              className="px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)] uppercase"
            />
            <input
              value={zipCode}
              onChange={e => setZipCode(e.target.value)}
              placeholder="ZIP"
              className="px-3 py-2 border border-[var(--store-border,#e5e7eb)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--store-accent,#2563eb)]"
            />
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--store-accent,#2563eb)] text-white text-sm font-medium disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save and continue
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-[var(--store-muted,#6b7280)] hover:text-gray-600"
        >
          Back to picker
        </button>
      </div>
    </form>
  );
}
